import { IconButton } from '@material-ui/core';
import { FC } from 'react';

import { useOptions } from '@agency/contexts/OptionsContext';
import { LettingTransactionRentReviewStateItem } from '@agency/modules/leases/pages/letting-manage/pages/letting-lease-events/LettingLeaseEvents.types';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Checkbox } from '@shared/components/checkbox';
import { DatePicker, MaterialDate } from '@shared/components/date-picker';
import { NumberField } from '@shared/components/number-field';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { TextField } from '@shared/components/text-field';
import { t } from '@shared/i18n';
import { CloseIcon } from '@shared/icons/close';
import { ButtonSize } from '@shared/types/common/button';
import { InputSize } from '@shared/types/common/input';

import { styles } from './DisposalRentReviewCard.styles';

export interface DisposalRentReviewCardProps extends WithStyles<typeof styles> {
  error: boolean;
  item: LettingTransactionRentReviewStateItem;
  leaseStartDate: MaterialDate;
  onDelete: () => void;
  onUpdate: (item: Partial<LettingTransactionRentReviewStateItem>) => void;
}

const DisposalRentReviewCardComponent: FC<DisposalRentReviewCardProps> = ({
  classes,
  error,
  item,
  leaseStartDate,
  onDelete,
  onUpdate,
}) => {
  const { reminderDaysOptions } = useOptions();

  const hasDatePriority = !!item.date_years || !!item.date_months || !!item.date_days;
  const showDatePriorityError = error && hasDatePriority && !leaseStartDate;

  return (
    <div className={classes.root}>
      <div className={classes.cardHeader}>
        <IconButton onClick={onDelete} className={classes.deleteButton}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.grid}>
        <div className={classes.field}>
          <span className={classes.label}>{t('rent_review_date_1')}</span>
          <DatePicker
            fullWidth
            disabled={item.date_priority === true}
            placeholder="DD/MM/YYYY"
            size={InputSize.medium}
            value={item.from}
            onChange={(value) =>
              onUpdate({
                from: value,
                date_priority: !value ? null : false,
              })
            }
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('years_months_days_to_review')}</span>
          <div style={{ display: 'flex', gap: '16px' }}>
            <NumberField
              fullWidth
              InputProps={{ endAdornment: t('years') }}
              min={0}
              error={showDatePriorityError}
              size={InputSize.medium}
              disabled={item.date_priority === false}
              value={item.date_years ?? ''}
              onChange={(value) =>
                onUpdate({
                  date_years: value,
                  date_priority: !!value || !!item.date_months || !!item.date_days ? true : null,
                })
              }
            />
            <NumberField
              fullWidth
              InputProps={{ endAdornment: t('months') }}
              min={0}
              error={showDatePriorityError}
              size={InputSize.medium}
              disabled={item.date_priority === false}
              value={item.date_months ?? ''}
              onChange={(value) =>
                onUpdate({
                  date_months: value,
                  date_priority: !!value || !!item.date_years || !!item.date_days ? true : null,
                })
              }
            />
            <NumberField
              fullWidth
              InputProps={{ endAdornment: t('days') }}
              min={0}
              error={showDatePriorityError}
              size={InputSize.medium}
              disabled={item.date_priority === false}
              value={item.date_days ?? ''}
              onChange={(value) =>
                onUpdate({
                  date_days: value,
                  date_priority: !!value || !!item.date_years || !!item.date_months ? true : null,
                })
              }
            />
          </div>
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('upward_only')}</span>
          <Checkbox
            classes={{ label: classes.label }}
            color="primary"
            label={t('yes')}
            checked={item.upward_only}
            onChange={(value) => onUpdate({ upward_only: value })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('reminder')}</span>
          <SingleSelect
            classes={{ root: classes.select }}
            placeholder={t('xtextx_ellipsis', { text: t('select') })}
            size={ButtonSize.medium}
            value={item.reminder_days}
            options={reminderDaysOptions}
            onChange={(value) => onUpdate({ reminder_days: Number(value) })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('comments')}</span>
          <TextField
            classes={{ root: classes.eventTextField }}
            multiline
            fullWidth
            minRows={1}
            placeholder={t('add_a_comment')}
            value={item.comments}
            onChange={(e) => onUpdate({ comments: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

export const DisposalRentReviewCard = withStyles(styles)(DisposalRentReviewCardComponent);
